import React, { useState, useCallback, useEffect } from 'react';
import { CustomViewerButton } from 'features/common';
import { makeStyles } from '@mui/styles';
import T from 'i18n';
import clsx from 'clsx';
import CommentInput from './CommentInput';
import RedactionInput from './RedactionInput';
import LinkInput from './LinkInput';
import { convert, fixPosition } from '../../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCopy,
  faLink,
  faHighlighter,
  faComment,
  faRetweet,
  faSquare,
  faImage,
  faStamp,
} from '@fortawesome/free-solid-svg-icons';
import CrossRefInput from './CrossRefInput';
import TagSelect from '../../TagSelect';
import { Mark } from '@prizmdoc/viewer-core';
import throttle from 'lodash.throttle';
import { useSelector } from 'react-redux';
import { selectEnableRedaction, selectTimeline } from 'common/selectors';
import AddEvent from './AddEvent';

const useStyles = makeStyles(() => ({
  popupContainer: {
    backgroundColor: 'white',
    boxShadow:
      '0px 5px 5px -3px rgb(0 0 0 / 20%), 0px 8px 10px 1px rgb(0 0 0 / 14%), 0px 3px 14px 2px rgb(0 0 0 / 12%)',
    borderRadius: 0,
    alignItems: 'center',
    width: '25rem',
    zIndex: 999999,
  },
  markButtonContainer: {
    position: 'fixed',
    visibility: 'hidden',
    opacity: 0,
    display: 'flex',
    borderRadius: '0.5rem',
    zIndex: 100,
    transform: 'translateY(-20px)', // Start slightly above to prevent flicker
    transition:
      'opacity 200ms ease-in-out, transform 200ms ease-in-out, visibility 0ms linear 200ms',

    '&>:first-child': {
      flexGrow: 1,
      borderTopLeftRadius: '0.5rem',
      borderBottomLeftRadius: '0.5rem',
    },

    '&>:last-child': {
      borderTopRightRadius: '0.5rem',
      borderBottomRightRadius: '0.5rem',
    },

    '&.visible': {
      opacity: 1,
      visibility: 'visible',
      transform: 'translateY(0)',
      transition:
        'opacity 200ms ease-in-out, transform 200ms ease-in-out, visibility 0ms linear 0ms',
    },
  },

  markButtonsContainer: {
    position: 'fixed',
    visibility: 'hidden',
    opacity: 0,
    display: 'flex',
    borderRadius: '0.5rem',
    zIndex: 100,
    transform: 'translateX(-20px)', // Start slightly to the left to prevent flicker
    transition:
      'opacity 200ms ease-in-out, transform 200ms ease-in-out, visibility 0ms linear 200ms',

    '&>:first-child': {
      flexGrow: 1,
      borderTopLeftRadius: '0.5rem',
      borderBottomLeftRadius: '0.5rem',
    },

    '&>:last-child': {
      borderTopRightRadius: '0.5rem',
      borderBottomRightRadius: '0.5rem',
    },

    '&.visible': {
      opacity: 1,
      visibility: 'visible',
      transform: 'translateX(0)',
      transition:
        'opacity 200ms ease-in-out, transform 200ms ease-in-out, visibility 0ms linear 0ms',
    },
  },
  markButton: {
    padding: '0.25rem',
    position: 'relative',
    transition: 'opacity 150ms, visibility 0ms 150ms',
    whiteSpace: 'nowrap',
    backgroundColor: '#4567e5',

    border: 'none',
    boxShadow: '0 4px 6px 0 #666',

    color: 'white',
    textAlign: 'center',
    opacity: '0.85',

    '> div': {
      fontSize: '1.2rem',
    },

    a: {
      color: 'white',
      '&:hover': {
        textDecoration: 'underline',
        cursor: 'pointer',
      },
    },

    "&[type='button']:hover, &div [type='button']:hover": {
      boxShadow: '0 4px 10px 0 #666',
      opacity: 1,
    },
  },
  noBorder: {
    border: 'none',
  },
  uploadIcon: {
    padding: '0.2rem',
    borderRadius: '50% !important',
    fontSize: '2rem',
    color: 'white',
    margin: '0.5rem',
    cursor: 'pointer',
    backgroundColor: '#4567e5',
  },
  disabled: {
    backgroundColor: '#A5A4A1',
  },
  error: {
    backgroundColor: 'red',
  },
}));

const Input = ({
  scrollContainer,
  mark,
  annotation,
  showCommentInput,
  showRedactionInput,
  updateHighlight,
  updateRedaction,
  deleteAnnotation,
  updateAnnotation,
  showLinkInput,
  updateLink,
  showCrossRefInput,
  updateCrossRefference,
  safeSelectMark,
}: any) => {
  return (
    <>
      {!!showCommentInput && (
        <CommentInput
          comment={mark.getData('comment')}
          selectedTags={annotation?.tags || []}
          selectedTimeline={annotation?.timeline}
          onClick={showCommentInput.addHighlight}
          update={showCommentInput.addHighlight === updateHighlight}
          deleteAnnotation={() => deleteAnnotation(mark)}
          createdBy={annotation?.createdBy}
          selectMark={() => {
            // defer handle grabs for now
            // mark.interactionMode = Mark.InteractionMode.Full;
            safeSelectMark(mark);
          }}
          page={scrollContainer}
          shared={annotation?.shared}
          onSharedClick={() => {
            updateAnnotation(
              annotation.annotation,
              annotation.id,
              annotation.tags,
              !annotation.shared,
            );
          }}
          itemText={mark?.getData('itemText') || mark?.text || ''}
        />
      )}
      {!!showLinkInput && (
        <LinkInput
          onClick={showLinkInput.addLink}
          update={showLinkInput.addLink === updateLink}
          deleteAnnotation={() => deleteAnnotation(mark)}
          createdBy={annotation?.createdBy}
          page={scrollContainer}
          link={mark?.getData('link') && JSON.parse(mark?.getData('link'))}
          linkText={mark?.getData('linkText')}
        />
      )}
      {!!showCrossRefInput && (
        <CrossRefInput
          onClick={showCrossRefInput.addCrossReference}
          crossRef={mark?.getData('crossRef')}
          link={mark?.getData('link') && JSON.parse(mark?.getData('link'))}
          linkText={mark?.getData('linkText')}
          update={showCrossRefInput.addCrossReference === updateCrossRefference}
          deleteAnnotation={() => deleteAnnotation(mark)}
          createdBy={annotation?.createdBy}
          href={mark?.getData('href')}
          selectMark={() => {
            // defer handle grabs for now
            // mark.interactionMode = Mark.InteractionMode.Full;
            safeSelectMark(mark);
          }}
          page={scrollContainer}
          burned={annotation?.burned}
          crossRefFileId={mark?.getData('linkId')}
        />
      )}

      {!!showRedactionInput && (
        <RedactionInput
          reason={mark.getData('reason')}
          onClick={showRedactionInput.addRedaction}
          update={showRedactionInput.addRedaction === updateRedaction}
          deleteAnnotation={() => deleteAnnotation(mark)}
          createdBy={annotation?.createdBy}
          selectMark={() => {
            // defer handle grabs for now
            // mark.interactionMode = Mark.InteractionMode.Full;
            safeSelectMark(mark);
          }}
          page={scrollContainer}
        />
      )}
    </>
  );
};

const Buttons = ({
  mark,
  addHighlight,
  addRedaction,
  canCreateAndEditPublicHyperlinks,
  setShowCommentInput,
  setShowRedactionInput,
  setShowLinkInput,
  addLink,
  addRectangleHighlight,
  addRectangleRedaction,
  canCreateAndEditCrossRefs,
  setShowCrossRefInput,
  addCrossReference,
  addTimelineAnnotationHandler,
  copyText,
  setHideMarkButtons,
  annotation,
  markToBeCreated,
  addEventHandler,
  fileId,
  copyable,
}: any) => {
  const isText = mark.type === Mark.Type.HighlightAnnotation;
  const isRectangle = mark.type === Mark.Type.RectangleAnnotation;
  const isTimeline = useSelector(selectTimeline);
  const enableRedaction = useSelector(selectEnableRedaction);

  // Check if this mark was created from handleItemClick (SearchAnswerContainer)
  const isMarkButtonTrigger = mark && mark.getData('subType') === 'markButtonTrigger';
  const classes = useStyles();
  return (
    <>
      {isText && (
        <CustomViewerButton onClick={() => addHighlight()} className={classes.markButton}>
          <FontAwesomeIcon style={{ margin: '0 0.25rem' }} icon={faHighlighter} />
          {T.translate('case.addHighlight')}
        </CustomViewerButton>
      )}
      {isText && (
        <CustomViewerButton
          onClick={() => setShowCommentInput({ addHighlight })}
          className={classes.markButton}
        >
          <FontAwesomeIcon style={{ margin: '0 0.25rem' }} icon={faComment} />
          {T.translate('case.addComment')}
        </CustomViewerButton>
      )}
      {isText && canCreateAndEditPublicHyperlinks && !isMarkButtonTrigger && (
        <CustomViewerButton
          onClick={() => setShowLinkInput({ addLink })}
          className={classes.markButton}
        >
          <FontAwesomeIcon style={{ margin: '0 0.25rem' }} icon={faLink} />
          {T.translate('case.addLink')}
        </CustomViewerButton>
      )}
      {isText && enableRedaction && (
        <CustomViewerButton
          onClick={() => setShowRedactionInput({ addRedaction })}
          className={classes.markButton}
        >
          <FontAwesomeIcon style={{ margin: '0 0.25rem' }} icon={faStamp} />
          {T.translate('case.addRedaction')}
        </CustomViewerButton>
      )}
      {isRectangle && (
        <CustomViewerButton onClick={addRectangleHighlight} className={classes.markButton}>
          <FontAwesomeIcon style={{ margin: '0 0.25rem' }} icon={faImage} />
          {T.translate('case.addHighlight')}
        </CustomViewerButton>
      )}
      {isRectangle && (
        <CustomViewerButton
          onClick={() => setShowCommentInput({ addHighlight: addRectangleHighlight })}
          className={classes.markButton}
        >
          <FontAwesomeIcon style={{ margin: '0 0.25rem' }} icon={faSquare} />
          {T.translate('case.addComment')}
        </CustomViewerButton>
      )}
      {isRectangle && canCreateAndEditCrossRefs && (
        <CustomViewerButton
          onClick={() => setShowCrossRefInput({ addCrossReference })}
          className={classes.markButton}
        >
          <FontAwesomeIcon style={{ margin: '0 0.25rem' }} icon={faRetweet} />
          {T.translate('case.addCrossReference')}
        </CustomViewerButton>
      )}
      {isRectangle && enableRedaction && (
        <CustomViewerButton
          onClick={() => setShowRedactionInput({ addRedaction: addRectangleRedaction })}
          className={classes.markButton}
        >
          <FontAwesomeIcon style={{ margin: '0 0.25rem' }} icon={faStamp} />
          {T.translate('case.addRedaction')}
        </CustomViewerButton>
      )}
      <TagSelect
        startOpen={false}
        onOpen={() => {
          setHideMarkButtons(true);
        }}
        selectedTags={annotation?.tags || []}
        updateSelectedTag={
          markToBeCreated &&
          ((selectedTags: any) => {
            if (mark.type === Mark.Type.RectangleAnnotation) {
              addRectangleHighlight({ tags: selectedTags });
            } else {
              addHighlight({ tags: selectedTags });
            }
          })
        }
      />
      {isText && isTimeline && (
        <AddEvent
          onOpen={() => {
            setHideMarkButtons(true);
          }}
          mark={mark}
          addEventHandler={addEventHandler}
          addTimelineAnnotationHandler={addTimelineAnnotationHandler}
          fileId={fileId}
        />
      )}
      {isText && !isMarkButtonTrigger && !copyable && (
        <CustomViewerButton
          onClick={copyText}
          className={classes.markButton}
          style={{ borderLeft: '1px dashed white' }}
        >
          <FontAwesomeIcon style={{ margin: '0 0.25rem' }} icon={faCopy} />
          {T.translate('generic.copyText')}
        </CustomViewerButton>
      )}
    </>
  );
};

export default ({
  markToBeCreated,
  markToBeUpdated,
  updateAnnotation,
  highlights,
  viewerControl,
  scrollContainer,
  addHighlight,
  addRedaction,
  updateHighlight,
  updateRedaction,
  updateCrossRefference,
  updateLink,
  copyText,
  addLink,
  addRectangleHighlight,
  addRectangleRedaction,
  addCrossReference,
  addTimelineAnnotationHandler,
  deleteAnnotation,
  canCreateAndEditCrossRefs,
  canCreateAndEditPublicHyperlinks,
  addEventHandler,
  fileId,
  copyable,
}: any) => {
  const mark = markToBeCreated || markToBeUpdated;
  const enableRedaction = useSelector(selectEnableRedaction);

  const [, _updateState] = useState<any>();
  const updateState = throttle(() => _updateState({}), 50);
  const forceUpdate = useCallback(() => updateState(), [updateState]);
  const [showCommentInput, setShowCommentInput] = useState<{
    addHighlight: (props: any) => void;
  } | null>(null);
  const [showLinkInput, setShowLinkInput] = useState<{
    addLink: (props: any) => void;
  } | null>(null);
  const [showCrossRefInput, setShowCrossRefInput] = useState<{
    addCrossReference: (props: any) => void;
  } | null>(null);
  const [showRedactionInput, setShowRedactionInput] = useState<{
    addRedaction: (props: any) => void;
  } | null>(null);

  const [hideAll, setHideAll] = useState<boolean>(false);
  const [hideMarkButtons, setHideMarkButtons] = useState<boolean>(false);
  const [isReady, setIsReady] = useState<boolean>(false); // Track if mark is ready to show buttons
  const [forceHide, setForceHide] = useState<boolean>(false); // Force hide during navigation

  const classes = useStyles();

  // Safe mark selection function to prevent "Mark that is not on the document" errors
  const safeSelectMark = useCallback(
    (markToSelect: any) => {
      if (
        !markToSelect ||
        !viewerControl ||
        !viewerControl.getAllMarks ||
        !viewerControl.selectMarks
      ) {
        return;
      }

      // For markButtonTrigger marks, skip the selection process as they may not be fully registered
      const isMarkButtonTrigger =
        markToSelect.getData && markToSelect.getData('subType') === 'markButtonTrigger';
      if (isMarkButtonTrigger) {
        return;
      }

      const trySelectMark = (retryCount = 0, maxRetries = 3) => {
        try {
          // Verify mark is still attached to the document
          const allMarks = viewerControl.getAllMarks();
          const markExists = allMarks.some((m: any) => m.uid === markToSelect.uid);

          if (!markExists) {
            if (retryCount < maxRetries) {
              setTimeout(() => trySelectMark(retryCount + 1, maxRetries), 100 * (retryCount + 1)); // Exponential backoff
            }
            return;
          }

          viewerControl.selectMarks([markToSelect]);
        } catch {
          if (retryCount < maxRetries) {
            setTimeout(() => trySelectMark(retryCount + 1, maxRetries), 100 * (retryCount + 1)); // Exponential backoff
          }
        }
      };

      // Start with a small initial delay to ensure mark is fully attached
      setTimeout(() => trySelectMark(), 100);
    },
    [viewerControl],
  );

  useEffect(() => {
    if (!mark) return;

    // Enhanced force update with retry logic for positioning issues
    const enhancedForceUpdate = () => {
      try {
        // For markButtonTrigger marks, use simplified positioning check
        const isMarkButtonTrigger = mark.getData && mark.getData('subType') === 'markButtonTrigger';
        if (isMarkButtonTrigger) {
          // Simple validation for markButtonTrigger marks
          if (!mark.boundingRectangle || !mark.pageNumber) {
            setTimeout(() => forceUpdate(), 100);
            return;
          }

          // For markButtonTrigger, just trigger update - positioning will be handled by convert()
          forceUpdate();
          return;
        }

        // Original complex validation for non-markButtonTrigger marks
        // Validate mark has required properties before converting
        if (!mark.boundingRectangle || !mark.pageNumber) {
          return;
        }

        // Verify mark is still attached to the document
        if (viewerControl && viewerControl.getAllMarks) {
          const allMarks = viewerControl.getAllMarks();
          const markExists = allMarks.some((m: any) => m.uid === mark.uid);

          if (!markExists) {
            // Retry after a short delay to allow mark registration to complete
            setTimeout(() => {
              try {
                const retryMarks = viewerControl.getAllMarks();
                const retryMarkExists = retryMarks.some((m: any) => m.uid === mark.uid);
                if (retryMarkExists) {
                  const retryConvertResult = convert(mark, viewerControl);
                  if (
                    retryConvertResult &&
                    retryConvertResult.clientX !== undefined &&
                    retryConvertResult.clientY !== undefined
                  ) {
                    forceUpdate();
                  }
                }
              } catch (retryError) {
                console.warn('Error during mark verification retry:', retryError);
              }
            }, 100);
            return;
          }
        }

        const convertResult = convert(mark, viewerControl);

        if (!convertResult) {
          return;
        }

        const { clientX, clientY } = convertResult;

        if (clientX !== undefined && clientY !== undefined && !isNaN(clientX) && !isNaN(clientY)) {
          forceUpdate();
        } else {
          // If positioning data is invalid, try again after a short delay
          setTimeout(() => {
            try {
              const retryResult = convert(mark, viewerControl);
              if (
                retryResult &&
                retryResult.clientX !== undefined &&
                retryResult.clientY !== undefined
              ) {
                forceUpdate();
              }
            } catch (e) {
              console.warn('Retry positioning failed:', e);
            }
          }, 100);
        }
      } catch {
        forceUpdate(); // Fallback to regular update
      }
    };

    // Enhanced force update with additional retry for page load events
    const enhancedForceUpdateWithRetry = () => {
      enhancedForceUpdate();

      // For markButtonTrigger marks, no need for additional complex retries
      const isMarkButtonTrigger = mark.getData && mark.getData('subType') === 'markButtonTrigger';
      if (isMarkButtonTrigger) {
        // Just one simple retry for markButtonTrigger
        setTimeout(() => {
          enhancedForceUpdate();
        }, 200);
      }
    };

    // for to redraw on scroll
    scrollContainer.addEventListener('scroll', enhancedForceUpdate);
    // for to redraw on resize
    window.addEventListener('resize', enhancedForceUpdate);
    viewerControl.on('ScaleChanged', enhancedForceUpdate);
    viewerControl.on('DocumentRotated', enhancedForceUpdate);

    // Listen for hide event from SearchAnswerContainer
    const handleHideMarkButtons = () => {
      setForceHide(true);
      setIsReady(false);
    };

    // Listen for page load completion to update positioning for markButtonTrigger
    const handlePageLoaded = () => {
      enhancedForceUpdateWithRetry();
    };

    // Listen for page display events which indicate the page is ready for coordinate conversion
    const handlePageDisplayed = () => {
      enhancedForceUpdateWithRetry();
    };

    if (typeof viewerControl.on === 'function') {
      viewerControl.on('hideMarkButtons', handleHideMarkButtons);
      viewerControl.on('PageLoaded', handlePageLoaded);
      viewerControl.on('PageDisplayed', handlePageDisplayed);
      viewerControl.on('DocumentLoaded', handlePageLoaded);
    }

    return () => {
      window.removeEventListener('resize', enhancedForceUpdate);
      scrollContainer.removeEventListener('scroll', enhancedForceUpdate);
      viewerControl.off('ScaleChanged', enhancedForceUpdate);
      viewerControl.off('DocumentRotated', enhancedForceUpdate);
      if (typeof viewerControl.off === 'function') {
        viewerControl.off('hideMarkButtons', handleHideMarkButtons);
        viewerControl.off('PageLoaded', handlePageLoaded);
        viewerControl.off('PageDisplayed', handlePageDisplayed);
        viewerControl.off('DocumentLoaded', handlePageLoaded);
      }
    };
  }, [mark, forceUpdate, scrollContainer, viewerControl]);

  useEffect(() => {
    if (!mark) return;

    setShowCommentInput(
      markToBeUpdated &&
        (markToBeUpdated.type === Mark.Type.RectangleAnnotation ||
          markToBeUpdated.type === Mark.Type.HighlightAnnotation) && {
          addHighlight: updateHighlight,
        },
    );
    setShowLinkInput(
      canCreateAndEditPublicHyperlinks &&
        markToBeUpdated &&
        markToBeUpdated.type === Mark.Type.TextHyperlinkAnnotation && {
          addLink: updateLink,
        },
    );

    setShowCrossRefInput(
      markToBeUpdated &&
        markToBeUpdated.type === Mark.Type.TextAnnotation && {
          addCrossReference: updateCrossRefference,
        },
    );

    setShowRedactionInput(
      enableRedaction &&
        markToBeUpdated &&
        (markToBeUpdated.type === Mark.Type.TextSelectionRedaction ||
          markToBeUpdated.type === Mark.Type.RectangleRedaction) && {
          addRedaction: updateRedaction,
        },
    );

    setHideAll(false);
    setHideMarkButtons(false);
    setForceHide(false); // Reset force hide when new mark appears

    // For markButtonTrigger marks, be more lenient with readiness
    const isMarkButtonTrigger = mark.getData && mark.getData('subType') === 'markButtonTrigger';
    if (isMarkButtonTrigger) {
      // For markButtonTrigger marks, show immediately since the user clicked on it
      setIsReady(true);

      // Simple positioning retry for markButtonTrigger marks
      const retryTimer = setTimeout(() => {
        forceUpdate(); // Just trigger a re-render to update positioning
      }, 100);

      return () => {
        clearTimeout(retryTimer);
      };
    }

    // Add small delay to ensure mark is fully rendered before showing buttons
    setIsReady(false);
    let retryCount = 0;
    const maxRetries = 3;

    const checkMarkReady = () => {
      if (!mark) return;

      try {
        // Validate mark has required properties before converting
        if (!mark.boundingRectangle || !mark.pageNumber) {
          if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(checkMarkReady, 200);
          } else {
            setIsReady(true); // Show anyway to prevent permanent hiding
          }
          return;
        }

        // Verify mark is still attached to the document
        if (viewerControl && viewerControl.getAllMarks) {
          const allMarks = viewerControl.getAllMarks();
          const markExists = allMarks.some((m: any) => m.uid === mark.uid);

          // For markButtonTrigger marks, be more lenient about document attachment
          const isMarkButtonTrigger =
            mark.getData && mark.getData('subType') === 'markButtonTrigger';

          if (!markExists && !isMarkButtonTrigger) {
            if (retryCount < maxRetries) {
              retryCount++;
              // Use shorter delay for readiness check retries since marks might still be registering
              setTimeout(checkMarkReady, 100);
            } else {
              setIsReady(false); // Don't show if mark is not attached
            }
            return;
          } else if (!markExists && isMarkButtonTrigger) {
            // For markButtonTrigger marks, if not found in document, just show anyway since user clicked
            setIsReady(true);
            return;
          }
        }

        // Check if mark has valid positioning data
        const convertResult = convert(mark, viewerControl);

        if (!convertResult) {
          // For markButtonTrigger marks, be more lenient - show the button even if conversion fails
          if (isMarkButtonTrigger) {
            setIsReady(true);
            return;
          }

          if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(checkMarkReady, 200);
          } else {
            setIsReady(true); // Show anyway to prevent permanent hiding
          }
          return;
        }

        const { clientWidth, clientX, clientY } = convertResult;
        const hasValidPosition =
          clientX !== undefined &&
          clientY !== undefined &&
          clientWidth > 0 &&
          !isNaN(clientX) &&
          !isNaN(clientY);

        if (hasValidPosition) {
          setIsReady(true);
        } else {
          // For markButtonTrigger marks, be more lenient with positioning validation
          if (isMarkButtonTrigger) {
            setIsReady(true);
            return;
          }

          if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(checkMarkReady, 200); // Retry after 200ms
          } else {
            setIsReady(true); // Show anyway to prevent permanent hiding
          }
        }
      } catch (error) {
        console.error('Error checking mark positioning:', error);
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(checkMarkReady, 200);
        } else {
          setIsReady(true); // Show anyway to prevent permanent hiding
        }
      }
    };

    const readyTimer = setTimeout(checkMarkReady, 100); // Initial check after 100ms

    return () => {
      clearTimeout(readyTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mark,
    markToBeUpdated,
    updateHighlight,
    updateCrossRefference,
    updateLink,
    canCreateAndEditCrossRefs,
    canCreateAndEditPublicHyperlinks,
    updateRedaction,
    enableRedaction,
  ]);

  // Reset state when mark disappears
  useEffect(() => {
    if (!mark) {
      setIsReady(false);
      setForceHide(false);
    }
  }, [mark]);

  // Listen for force hide events from SearchAnswerContainer
  useEffect(() => {
    if (!mark) return;

    const handleForceHide = (event: CustomEvent) => {
      if (event.detail.markId === mark.uid) {
        setForceHide(true);
        setIsReady(false);
      }
    };

    window.addEventListener('forceHideMarkButtons', handleForceHide as EventListener);

    return () => {
      window.removeEventListener('forceHideMarkButtons', handleForceHide as EventListener);
    };
  }, [mark?.uid]);

  const getStyle = useCallback(
    (el: HTMLElement | null, translateY: number) => {
      if (!el || !mark) return 'left: -100px; top: -100px;';

      try {
        const rectangle = el.getBoundingClientRect();

        // Validate mark has required properties before converting
        if (!mark.boundingRectangle || !mark.pageNumber) {
          return 'left: -100px; top: -100px;';
        }

        // For markButtonTrigger marks, be more lenient with positioning when document is loading
        const isMarkButtonTrigger = mark.getData && mark.getData('subType') === 'markButtonTrigger';

        // Verify mark is still attached to the document
        if (viewerControl && viewerControl.getAllMarks) {
          const allMarks = viewerControl.getAllMarks();
          const markExists = allMarks.some((m: any) => m.uid === mark.uid);

          // For markButtonTrigger marks, be more lenient about document attachment
          if (!markExists && !isMarkButtonTrigger) {
            return 'left: -100px; top: -100px;';
          }
        }

        const convertResult = convert(mark, viewerControl);

        // Check if conversion was successful
        if (!convertResult) {
          // For markButtonTrigger, try to position based on mark's own coordinates when convert fails
          if (isMarkButtonTrigger && mark.boundingRectangle) {
            // Enhanced fallback positioning that's more reliable
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            // Use mark's bounding rectangle as a guide
            const markRect = mark.boundingRectangle;
            let fallbackX = 150; // Safe default - moved right to avoid edge
            let fallbackY = 200; // Safe default - moved down to avoid top edge

            // Try to use the mark's position if available
            if (markRect && typeof markRect.x === 'number' && typeof markRect.y === 'number') {
              try {
                // Try to get page information for better scaling
                const pageElements = document.querySelectorAll('[data-pcc-pagename]');
                let pageScale = 1;

                if (pageElements.length > 0) {
                  const currentPageElement = Array.from(pageElements).find((el) =>
                    el.getAttribute('data-pcc-pagename')?.includes(`Page${mark.pageNumber}`),
                  );

                  if (currentPageElement) {
                    const pageRect = currentPageElement.getBoundingClientRect();
                    if (pageRect.width > 0) {
                      pageScale = pageRect.width / 1000; // Assume standard page is ~1000px
                    }
                  }
                }

                // Use calculated scale or fallback
                const scaleX = pageScale > 0 ? pageScale : Math.min(1.5, viewportWidth / 1200);
                const scaleY = pageScale > 0 ? pageScale : Math.min(1.2, viewportHeight / 1600);

                fallbackX = Math.max(
                  50,
                  Math.min(markRect.x * scaleX, viewportWidth - rectangle.width - 100),
                );
                fallbackY = Math.max(
                  50,
                  Math.min(markRect.y * scaleY, viewportHeight - rectangle.height - 50),
                );
              } catch (scaleError) {
                // Simple fallback
                const scaleX = Math.min(1.2, viewportWidth / 1200);
                const scaleY = Math.min(1.0, viewportHeight / 1600);

                fallbackX = Math.max(
                  50,
                  Math.min(markRect.x * scaleX, viewportWidth - rectangle.width - 100),
                );
                fallbackY = Math.max(
                  50,
                  Math.min(markRect.y * scaleY, viewportHeight - rectangle.height - 50),
                );
              }
            } else {
              // Use safe viewport-based positioning
              fallbackX = Math.min(200, viewportWidth * 0.15);
              fallbackY = Math.min(250, viewportHeight * 0.2);
            }

            // Ensure the position is within safe bounds with more generous margins
            if (fallbackX < 50 || fallbackX > viewportWidth - 400) {
              fallbackX = Math.min(180, viewportWidth * 0.15);
            }
            if (fallbackY < 50 || fallbackY > viewportHeight - 150) {
              fallbackY = Math.min(220, viewportHeight * 0.18);
            }

            return `left: ${fallbackX}px; top: ${fallbackY}px;`;
          }
          return 'left: -100px; top: -100px;';
        }

        const { clientWidth, clientX, clientY } = convertResult;

        // Check if we have valid positioning data
        if (clientX === undefined || clientY === undefined || isNaN(clientX) || isNaN(clientY)) {
          return 'left: -100px; top: -100px;';
        }

        let x, y;

        // Position mark buttons directly above the highlighted text for markButtonTrigger
        if (isMarkButtonTrigger) {
          x = Math.max(20, clientX + clientWidth / 2 - rectangle.width / 2); // Center buttons above the highlight
          y = Math.max(20, clientY - 40); // Position above the highlight
        } else {
          // Center the mark buttons as before for regular marks
          x = clientX + clientWidth / 2 - rectangle.width / 2;
          y = clientY - translateY;
        }

        const { x: fixedX, y: fixedY } = fixPosition({ x, y }, rectangle);

        // Additional validation for final position
        if (isNaN(fixedX) || isNaN(fixedY)) {
          return 'left: -100px; top: -100px;';
        }

        return `left: ${fixedX}px; top: ${fixedY}px;`;
      } catch (error) {
        console.error('Error calculating mark button position:', error, { markId: mark?.uid });
        return 'left: -100px; top: -100px;';
      }
    },
    [mark, viewerControl],
  );

  const annotation = mark && highlights.find((item: any) => item.annotation.uid === mark.uid);

  const isHighlightEvent = mark && mark.getData('subType') === 'highlightEvent';
  const isMarkButtonTrigger = mark && mark.getData('subType') === 'markButtonTrigger';

  if (!mark) return null;

  // Don't show MarkButton for highlightEvent marks unless they are specifically created to trigger MarkButton
  if (isHighlightEvent && !isMarkButtonTrigger) return null;

  return showCommentInput || showLinkInput || showCrossRefInput || showRedactionInput ? (
    <div
      ref={(el: any) => {
        el?.setAttribute(
          'style',
          `color: black; ${getStyle(el, (mark?.boundingRectangle?.height || 20) / -2)}`,
        );
      }}
      key={mark.uid}
      className={clsx(classes.markButtonContainer, classes.popupContainer, {
        visible: !!mark && !hideAll && isReady && !forceHide,
      })}
    >
      <Input
        viewerControl={viewerControl}
        scrollContainer={scrollContainer}
        mark={mark}
        annotation={annotation}
        showCommentInput={showCommentInput}
        showRedactionInput={showRedactionInput}
        updateHighlight={updateHighlight}
        updateRedaction={updateRedaction}
        deleteAnnotation={deleteAnnotation}
        updateAnnotation={updateAnnotation}
        showLinkInput={showLinkInput}
        updateLink={updateLink}
        showCrossRefInput={showCrossRefInput}
        updateCrossRefference={updateCrossRefference}
        safeSelectMark={safeSelectMark}
      />
    </div>
  ) : (
    <>
      {/* Mark Buttons Container - Show directly without toggle for markButtonTrigger */}
      <div
        key={`${mark.uid}-buttons`}
        className={clsx(
          isMarkButtonTrigger ? classes.markButtonsContainer : classes.markButtonContainer,
          { visible: !!mark && !hideAll && !hideMarkButtons && isReady && !forceHide },
        )}
        ref={(el) => {
          el?.setAttribute('style', getStyle(el, isMarkButtonTrigger ? 50 : 30));
        }}
      >
        <Buttons
          mark={mark}
          addHighlight={addHighlight}
          addRedaction={addRedaction}
          canCreateAndEditPublicHyperlinks={canCreateAndEditPublicHyperlinks}
          setShowCommentInput={setShowCommentInput}
          setShowRedactionInput={setShowRedactionInput}
          setShowLinkInput={setShowLinkInput}
          addLink={addLink}
          addRectangleHighlight={addRectangleHighlight}
          addRectangleRedaction={addRectangleRedaction}
          addTimelineAnnotationHandler={addTimelineAnnotationHandler}
          canCreateAndEditCrossRefs={canCreateAndEditCrossRefs}
          setShowCrossRefInput={setShowCrossRefInput}
          addCrossReference={addCrossReference}
          copyText={copyText}
          setHideAll={setHideAll}
          setHideMarkButtons={setHideMarkButtons}
          annotation={annotation}
          markToBeCreated={markToBeCreated}
          updateAnnotation={updateAnnotation}
          addEventHandler={addEventHandler}
          fileId={fileId}
          copyable={copyable}
        />
      </div>
    </>
  );
};
